import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';

// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import '../MainView/MainView.css';
import '../../assets/css/style.css';

import * as MAP_CONSTANTS from "../../common/constants/MapOptions.js";
import { getAuthToken, PIXEL_SERVER } from "../../common/utils/apiService.jsx";
import * as mapActions from "../../slices/mapSlice.js";
import * as searchActions from "../../slices/searchSlice.js";
import { flyToWithOffset } from "../../common/utils/helperFunctions.jsx";
import { highlightOrbitpath } from "../../slices/orderSlice.js";
import { searchStacData, searchSimilarStacData } from "../../services/stac.service.jsx";
import useGeoJsonLayer from "../../common/hooks/useGeoJsonLayer.js";
import useRasterLayer from "../../common/hooks/useRasterLayer.js";
import useOrbitLayer from "../../common/hooks/useOrbitLayer.js";
import useOrderPreview from "../../common/hooks/useOrderPreview.js";
import useMap from "../../common/hooks/useMap.js";

// map.queryRenderedFeatures() returns mapbox-gl's own Feature wrapper
// (carries _vectorTileFeature/_x/_y/_z internals) - strip it down to a plain
// GeoJSON Feature before it goes into a dispatched action, or the redux
// serializability check flags it on every map click.
const toPlainFeature = (feature) => feature && ({
    type: 'Feature',
    id: feature.id,
    properties: feature.properties,
    geometry: feature.geometry,
});

const MapView = () => {

    // const mapRef = useRef(null);
    // const drawRef = useRef(null);
    const mapContainerRef = useRef(null);

    const dispatch = useDispatch();

    const layers = useSelector(state => state.layers);
    // const featureCollection = useSelector(state => state.orderDetails.featureCollection);
    const searchResults = useSelector(state => state.search.searchResults);
    const hoveredResultIndex = useSelector(state => state.search.hoveredResultIndex);
    const detectedObject = useSelector(state => state.search.detectedObject);
    const geometry = useSelector(state => state.map.geometry);
    const coordinates = useSelector(state => state.map.coordinates);
    const uploadedCollection = useSelector(state => state.orderDetails.uploadedCollection);
    const selectedFeature = useSelector(state => state.search.selectedFeature);
    const selectedDectedObject = useSelector(state => state.search.selectedDectedObject);
    const searchedAoi = useSelector(state => state.map.searchedAoi);
    const displayView = useSelector(state => state.map.displayView);
    const analysisMode = useSelector(state => state.map.analysisMode);
    const multiAOI = useSelector(state => state.search.searchedMultiResults);
    const highlightName = useSelector(state => state.map.highlightName);
    const orderPreview = useSelector(state => state.map.orderPreview);
    const isOrdersRoute = useSelector(state => state.map.isOrdersRoute);

    const showOrbit = useSelector(state => state.orderDetails.showOrbit);
    const highlightOrbit = useSelector(state => state.orderDetails.highlightOrbit);

    mapboxgl.accessToken = 'pk.eyJ1IjoiYW50YXJpcy1tYXBib3giLCJhIjoiY2x2cGNubzF4MDBveTJtb2RtNG5zMGQ2NCJ9.MkPyl-z2FXuFSyYNwP_oaw';
    // mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    const openContextMenu = (point, feature) => {
        closeContextMenu();

        const menu = document.createElement('div');
        menu.id = 'map-context-menu';
        menu.style.position = 'absolute';
        menu.style.top = `${point.y}px`;
        menu.style.left = `${point.x}px`;
        menu.style.background = 'rgba(9, 9, 9, 0.80)';
        menu.style.borderRadius = '4px';
        menu.style.padding = '8px';
        menu.style.zIndex = 9999;
        menu.style.minWidth = '160px';
        menu.style.color = '#fff';
        menu.style.border = '0.5px solid rgba(240, 240, 240, 0.20)';
        menu.style.boxShadow = '0 4px 8px 0 rgba(0, 0, 0, 0.19)';
        menu.style.backdropFilter = 'blur(7.3px)';

        menu.innerHTML = `
          <div class="menu-item">Similar Images</div>
          <div class="menu-item">Place Order</div>
        `;

        menu.querySelectorAll('.menu-item').forEach(item => {
            item.style.padding = '6px 8px';
            item.style.cursor = 'pointer';
            item.onmouseenter = () => item.style.background = 'rgba(204, 245, 78, 0.10)';
            item.onmouseleave = () => item.style.background = 'transparent';
        });

        menu.children[0].onclick = () => {
            dispatch(searchSimilarStacData(feature.bbox));
            dispatch(mapActions.showSidebarRight());
            closeContextMenu();
        };

        menu.children[1].onclick = () => {
            console.log('Place Order', feature);
            closeContextMenu();
        };

        mapRef.current.getContainer().appendChild(menu);

        setTimeout(() => {
            document.addEventListener('click', closeContextMenu, { once: true });
        });
    };

    const closeContextMenu = () => {
        const existing = document.getElementById('map-context-menu');
        if (existing) existing.remove();
    };


    // const handler = (e) => {
    //     const clickedFeature = mapRef.current.queryRenderedFeatures(e.point, {
    //         layers: ['stac-polygon', 'stac-point'],
    //     });        
    //     dispatch(mapActions.showExports());
    //     dispatch(searchActions.setSelectedFeature(clickedFeature[0]));
    //     dispatch(searchActions.setActiveKey(clickedFeature[0]?.id?.toString()));
    //     dispatch(searchActions.disableOverlayLayer());
    //     dispatch(searchActions.setSelectedOverlayKey(null));

    //     mapRef.current.flyTo({ center: [e.lngLat.lng, e.lngLat.lat] });

    //     // temporary popup
    //     // new mapboxgl.Popup()
    //     //   .setLngLat(e.lngLat)
    //     //   .setHTML(e.features[0].properties.name)
    //     //   .addTo(newMap);
    // }

    // const detectHandler = (e) => {
    //     const clickedFeature = mapRef.current.queryRenderedFeatures(e.point, {
    //         layers: ['detected-stac-polygon', 'detectedstac-point'],
    //     });

    //     dispatch(searchActions.setSelectedDetectedObject(clickedFeature[0]));
    //     dispatch(searchActions.setDetectedKey(clickedFeature[0]?.id?.toString()));

    //     mapRef.current.flyTo({ center: [e.lngLat.lng, e.lngLat.lat] });
    // }

    const location = useLocation();
    let routeChanged = false;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        routeChanged = true;
    }, [location]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const { mapRef, drawRef } = useMap({
        containerRef: mapContainerRef,
        style: `mapbox://styles/mapbox/${layers.mapStyle.style}`,
        center: MAP_CONSTANTS.MAP_CENTER,
        zoom: MAP_CONSTANTS.MAP_DEFAULT_ZOOM,
        projection: MAP_CONSTANTS.MAP_VIEWS[0].dimension,
        onLoad: (map, draw) => {
            dispatch(mapActions.setMapRef(map));
            dispatch(mapActions.setDrawRef(draw));  // moved here from dispatchDrawRef
        },
        analysisMode,
        existingGeometry: geometry,
        onAOIChange: (geom) => {
            dispatch(mapActions.setGeometryDrawn(geom));
            dispatch(mapActions.setAOICoordinates(geom.coordinates));

            // Order creator reads geometry/coordinates directly off map state and
            // keeps the drawn shape on-screen via drawRef — it has no searchedAoi
            // outline layer, so it must not auto-search STAC or reset mode/display
            // view (that would delete the drawn shape and close the order modal).
            if (displayView === 'createOrderView') {
                return;
            }

            dispatch(mapActions.setSearchedAoi(geom));
            dispatch(mapActions.setSearchView());
            dispatch(searchStacData());
            // deferred: cleanUpDraw() calls drawRef.deleteAll() synchronously, which
            // must not run while mapbox-gl-draw is still mid-dispatch of this same
            // draw.create/draw.update event, or the just-drawn feature's render
            // layer sticks around alongside the new searchedAoi outline.
            setTimeout(() => {
                dispatch(mapActions.cleanUpDraw());
                dispatch(mapActions.resetMode());
                dispatch(mapActions.resetDisplayView());
            }, 0);
        },
        onMultiAOIChange: (collection) => {
            dispatch(mapActions.setMultiAOICoordinates(collection));
        },
    });

    useGeoJsonLayer(mapRef, {
        id: "search-aoi",
        data: searchedAoi,
        lineColor: "#FFFFFF",
        circleColor: "#FFFFFF",
    });

    // Orders and Explore both write into state.search.searchResults (Orders does this
    // so the existing setSelectedFeature lookup keeps working) - color by isOrdersRoute
    // (set by OrdersView on mount/unmount) so an order's item footprints read as
    // "this order's output", not a search result.
    const searchResultsColor = isOrdersRoute ? "#CCF54E" : "#5B49BC";

    useGeoJsonLayer(mapRef, {
        id: "stac",
        data: searchResults,
        fillColor: searchResultsColor,
        lineColor: searchResultsColor,
        circleColor: searchResultsColor,
        hoverable: true,
        clickable: false,
        highlightedFeatureId: hoveredResultIndex,
    });

    const webTileURL =
        selectedFeature?.collection && selectedFeature?.id
            ? `${PIXEL_SERVER}/px/webtile?collection=${selectedFeature.collection}&item=${selectedFeature.id}&z={z}&x={x}&y={y}&auth_token=${getAuthToken(true)}`
            : null;

    useRasterLayer(mapRef, {
        id: "preview",
        tileUrl: webTileURL,
        outlineFeature: selectedFeature?.geometry ? selectedFeature : null,
        onRightClick: (feature, e) => {
            openContextMenu(e.point, feature);
        },
    });

    const multiAOIFeatureCollection =
        multiAOI && multiAOI.length > 0
            ? {
                type: "FeatureCollection",
                features: multiAOI.map((item, index) => ({
                    type: "Feature",
                    properties: {
                        name: item.name,
                        matched: item.matched,
                        id: index,
                    },
                    geometry: item.geometry,
                })),
            }
            : null;

    useGeoJsonLayer(mapRef, {
        id: "multi-aoi",
        data: multiAOIFeatureCollection,
        lineColor: "#FFFFFF",
        circleColor: "#FFFFFF",
        clickable: true,
        onClick: (feature, e) => {
            new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setText(feature.properties.name)
                .addTo(mapRef.current);
        },
    });


    useGeoJsonLayer(mapRef, {
        id: "uploaded",
        data: uploadedCollection,
        fillColor: "#2C373A",
        lineColor: "#3AB2D0",
        circleColor: "#3AB2D0",
        fillOpacity: 0.5,
    });


    useOrbitLayer(mapRef, {
        passes: showOrbit?.satellitePasses,
        providerName: showOrbit?.providerName,
        highlightedEntryTime: highlightOrbit?.entryTime,
        onClick: (pass) => dispatch(highlightOrbitpath(pass)),
    });

    const orderPreviewFeatureCollection = (() => {
        if (!orderPreview) return null;

        // Multiple orders
        if (Array.isArray(orderPreview) && orderPreview.length > 0) {
            return {
                type: "FeatureCollection",
                features: orderPreview,
            };
        }

        // Single order feature
        if (orderPreview?.geometry) {
            return {
                type: "FeatureCollection",
                features: [orderPreview],
            };
        }

        return null;
    })();

    useGeoJsonLayer(mapRef, {
        id: "order-preview",
        data: orderPreviewFeatureCollection,
        fillColor: "#CCF54E",
        lineColor: "#CCF54E",
        circleColor: "#CCF54E",
        fillOpacity: 0.1,
    });

    useOrderPreview(mapRef, orderPreview);

    useGeoJsonLayer(mapRef, {
        id: "detected",
        data: detectedObject,
        fillColor: "#5B49BC",
        lineColor: "#5B49BC",
        circleColor: "#5B49BC",
        fillOpacity: 0.2,
        hoverable: true,
    });

    useGeoJsonLayer(mapRef, {
        id: "select-detected",
        data: selectedDectedObject,
        fillColor: "#00FF00",
        lineColor: "#00FF00",
        circleColor: "#00FF00",
        fillOpacity: 0.1,
    });


    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const queryTopFeature = (e, layerIds) => {
            const existing = layerIds.filter(id => map.getLayer(id));
            if (existing.length === 0) return null;
            const feats = map.queryRenderedFeatures(e.point, { layers: existing });
            return feats && feats.length > 0 ? feats[0] : null;
        };

        const handleMapClick = (e) => {
            if (!map.isStyleLoaded()) return;

            const detected = queryTopFeature(e, [
                'detected-fill',
                'detected-circle',
            ]);

            if (detected) {
                dispatch(searchActions.setDetectedObjectModal());
                dispatch(searchActions.setSelectedDetectedObject(toPlainFeature(detected)));
                dispatch(searchActions.setDetectedKey(detected?.id?.toString?.() || String(detected?.id || '')));
                flyToWithOffset(map, e, { x: -300, y: 0 });
                return;
            }

            const stac = queryTopFeature(e, [
                'stac-fill',
                'stac-circle',
            ]);

            if (stac) {
                dispatch(mapActions.showExports());
                dispatch(searchActions.setSelectedFeature(toPlainFeature(stac)));
                dispatch(searchActions.setActiveKey(stac?.id?.toString?.() || String(stac?.id || '')));
                dispatch(searchActions.disableOverlayLayer());
                dispatch(searchActions.setSelectedOverlayKey(null));
                dispatch(searchActions.resetSelectedDetectedObject());
                dispatch(mapActions.hideSidebarRight());
                dispatch(searchActions.clearSimilarResults());
                dispatch(searchActions.clearSelectedFeatureIds());
                flyToWithOffset(map, e, { x: -100, y: 0 });
                return;
            }
        };

        map.on('click', handleMapClick);

        return () => {
            map.off('click', handleMapClick);
        };
    }, [dispatch]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Small delay ensures map is fully initialized
        setTimeout(() => {
            map.jumpTo({
                center: MAP_CONSTANTS.MAP_CENTER,
                zoom: MAP_CONSTANTS.MAP_DEFAULT_ZOOM,
            });
        }, 50);
    }, []);   // <-- empty deps = run only when this PAGE mounts      

    return (<div id='map' ref={mapContainerRef}></div>)

};

export default MapView;
